import { ReactNode, useState } from "react";
import { TypedChartOfAccountsTemplateUpload } from "@common/helpers/chartOfAccountsTemplateTypeMapping";
import {
    MAX_CHART_OF_ACCOUNTS_TEMPLATE_ACCOUNTS,
    MAX_CHART_OF_ACCOUNTS_TEMPLATE_GROUPS,
} from "@common/constants/domains/accounting/chartOfAccountsTemplateAccount";
import { pluralizeWithCount } from "@common/helpers/string";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { UploadIcon } from "@icons";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ChartOfAccountsTemplateUploadSteps } from "../../upload/ChartOfAccountsTemplateUploadSteps";
import { useChartOfAccountsTemplateUploadFlow } from "../../upload/useChartOfAccountsTemplateUploadFlow";
import {
    mergeImportedChart,
    MergeImportedChartResult,
} from "../draftImportOperations";
import { createTempId } from "../tempId";
import { TemplateEditorDraft } from "../useTemplateEditorDraft";
import { TEMPLATE_DRAFT_ENTITY_ID } from "./templateDraftMapping";

interface Params {
    draft: TemplateEditorDraft;
}

interface TemplateImportTools {
    toolbarButton: ReactNode;
    overlays: ReactNode;
}

const describeImport = ({
    addedAccountCount,
    skippedAccountCount,
    clearedCodeCount,
}: MergeImportedChartResult): string =>
    [
        `Added ${pluralizeWithCount("account", addedAccountCount)}`,
        skippedAccountCount > 0 &&
            `skipped ${skippedAccountCount} already in the template`,
        clearedCodeCount > 0 &&
            `cleared ${pluralizeWithCount("clashing code", clearedCodeCount)}`,
    ]
        .filter(Boolean)
        .join(", ");

const TemplateImportFlow = ({
    onClose,
    onTypesConfirmed,
}: {
    onClose: () => void;
    onTypesConfirmed: (chart: TypedChartOfAccountsTemplateUpload) => void;
}) => {
    const flow = useChartOfAccountsTemplateUploadFlow();
    return (
        <ChartOfAccountsTemplateUploadSteps
            title="Import accounts"
            flow={flow}
            onClose={onClose}
            onTypesConfirmed={onTypesConfirmed}
        />
    );
};

export const useTemplateImportTools = ({
    draft,
}: Params): TemplateImportTools => {
    const [isImportOpen, setIsImportOpen] = useState(false);
    const { toast: successToast, error: errorToast } = useToaster();

    const handleTypesConfirmed = (
        chart: TypedChartOfAccountsTemplateUpload,
    ) => {
        const result = mergeImportedChart({
            state: { accounts: draft.accounts, groups: draft.groups },
            chart,
            entityId: TEMPLATE_DRAFT_ENTITY_ID,
            generateId: createTempId,
        });
        if (
            result.state.accounts.length >
                MAX_CHART_OF_ACCOUNTS_TEMPLATE_ACCOUNTS ||
            result.state.groups.length > MAX_CHART_OF_ACCOUNTS_TEMPLATE_GROUPS
        ) {
            errorToast(
                `Import would take the template past ${MAX_CHART_OF_ACCOUNTS_TEMPLATE_ACCOUNTS} accounts or ${MAX_CHART_OF_ACCOUNTS_TEMPLATE_GROUPS} groups`,
            );
            return;
        }
        draft.replaceState(result.state);
        setIsImportOpen(false);
        successToast(describeImport(result));
    };

    return {
        toolbarButton: (
            <Tooltip content="Import your accounts">
                <Button
                    variant="primary"
                    size="md"
                    iconOnly
                    aria-label="Import your accounts"
                    onClick={() => setIsImportOpen(true)}
                >
                    <UploadIcon />
                </Button>
            </Tooltip>
        ),
        overlays: isImportOpen && (
            <TemplateImportFlow
                onClose={() => setIsImportOpen(false)}
                onTypesConfirmed={handleTypesConfirmed}
            />
        ),
    };
};
